import { useBooleanLocalStorage } from '@sixfold/app-utils';
import { Button, Modal, ModalFooter, ModalHeader, ModalMain, Select, Stack, Text } from '@sixfold/common-ui';
import { Localized, i18n, useLocalization } from '@sixfold/localization-component';
import * as React from 'react';

import { useProductName } from '../../theme_wrapper';
import { useUserProfileMutation } from '../../user';
import { AvailableLanguage, supportedLanguages } from '../language_picker';

const MODAL_SEEN_KEY_PREFIX = 'language_welcome_modal_seen_v1';

type LanguageOption = { value: AvailableLanguage; label: string };

interface Props {
  userId: string;
  userLocale: string | null;
}

export const WelcomeLanguageModal: React.FunctionComponent<Props> = ({ userId, userLocale }) => {
  const PRODUCT_NAME = useProductName();
  const [hasSeen, setHasSeen] = useBooleanLocalStorage(`${MODAL_SEEN_KEY_PREFIX}_${userId}`, false);
  const languageLabel = useLocalization('component.languagepicker.language.title', 'Language');
  const changeUserProfile = useUserProfileMutation();

  const languageOptions = React.useMemo<LanguageOption[]>(
    () =>
      (Object.keys(supportedLanguages) as AvailableLanguage[]).map((lang) => ({
        value: lang,
        label: supportedLanguages[lang],
      })),
    [],
  );

  const [selectedOption, setSelectedOption] = React.useState<LanguageOption>(
    () =>
      (userLocale !== null ? languageOptions.find((opt) => opt.value === userLocale) : undefined) ??
      languageOptions.find((opt) => opt.value === i18n.language) ??
      languageOptions.find((opt) => opt.value === 'en') ??
      languageOptions[0],
  );

  const handleLanguageChange = React.useCallback((option: LanguageOption | null) => {
    if (option === null) return;
    setSelectedOption(option);
  }, []);

  const handleContinue = React.useCallback(async () => {
    await changeUserProfile({ locale: selectedOption.value });
    if (i18n.isInitialized) {
      await i18n.changeLanguage(selectedOption.value);
    }
    setHasSeen(true);
  }, [changeUserProfile, selectedOption, setHasSeen]);

  if (hasSeen) {
    return null;
  }

  return (
    <Modal size="small" open={true} trigger={null} onToggle={(open) => !open && handleContinue()}>
      <ModalHeader
        title={<Localized id="component.welcomeLanguageModal.title">Welcome to {{ PRODUCT_NAME }}</Localized>}
      />
      <ModalMain>
        <Stack direction="column" gap="medium">
          <Text>
            <Localized id="component.welcomeLanguageModal.description">
              Select your preferred language to continue.
            </Localized>
          </Text>
          <Select<LanguageOption>
            name="language"
            label={languageLabel}
            labelPosition="block"
            value={selectedOption}
            options={languageOptions}
            onChange={handleLanguageChange}
            helpMessage={
              <Localized id="component.welcomeLanguageModal.helpMessage">
                {'Change language anytime in Account actions > Language.'}
              </Localized>
            }
            usePortal
          />
        </Stack>
      </ModalMain>
      <ModalFooter>
        <Stack justifyContent="end">
          <Button kind="primary" size="medium" onClick={handleContinue}>
            <Localized id="component.welcomeLanguageModal.confirm">Confirm</Localized>
          </Button>
        </Stack>
      </ModalFooter>
    </Modal>
  );
};
